import React, { useCallback, useMemo, useRef } from "react";
import { connect } from "react-redux";

import { faReply } from "@fortawesome/pro-solid-svg-icons/faReply";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { isToday } from "date-fns";
import orderBy from "lodash-es/orderBy";
import { useIntl } from "react-intl";

import EmailFolder from "@mapmycustomers/shared/enum/EmailFolder";
import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";

import EmailMetrics from "@app/component/EmailMetrics";
import { RootState } from "@app/store/rootReducer";
import { formatDate } from "@app/util/formatters";

import { openParentEmailThread } from "../store/actions";
import { getParentEmailThreadsFolder } from "../store/selectors";
import getAllEmailRecipients from "../util/getAllEmailRecipients";
import getEmailDate from "../util/getEmailDate";
import useEmailUser from "../util/useEmailUser";
import useGetEmailRecipientName from "../util/useGetEmailRecipientName";
import useGetEmailSenderName from "../util/useGetEmailSenderName";
import useParentEmailThreadsFolderAnalytics from "../util/useParentEmailThreadsFolderAnalytics";

import EmailActivity from "./EmailActivity";
import EmailAttachmentsIcon from "./EmailAttachmentsIcon";
import EmailUser from "./EmailUser";
import messages from "./messages";
import styles from "./ParentEmail.module.scss";
import ParentEmailBodyPreview from "./ParentEmailBodyPreview";
import SearchTextHighlighter from "./SearchTextHighlighter";

interface ParentEmailProps {
  emailFolder: EmailFolder;
  emailThread: EmailThread;
  onOpenEmailThread: typeof openParentEmailThread;
}

const ParentEmail: React.FC<ParentEmailProps> = ({
  emailFolder,
  emailThread,
  onOpenEmailThread,
}) => {
  const intl = useIntl();
  const analytics = useParentEmailThreadsFolderAnalytics();

  const emailUser = useEmailUser(emailThread);

  const getEmailRecipientName = useGetEmailRecipientName();
  const getEmailSenderName = useGetEmailSenderName();

  const containerRef = useRef<HTMLDivElement>(null);

  const recipients = useMemo(() => {
    const me = intl.formatMessage(messages.meRecipient);
    const senderEmail = emailThread.from[0]!.email;

    switch (emailFolder) {
      case EmailFolder.INBOX: {
        if (emailThread.messageCount === 1) {
          return `${getEmailSenderName(emailThread)}`;
        } else {
          return [
            getEmailSenderName(emailThread),
            ...orderBy(
              getAllEmailRecipients(emailThread)
                .filter((recipientAddress) => recipientAddress.email !== senderEmail)
                .map((recipientAddress) => getEmailRecipientName(recipientAddress)),
              [(recipient) => recipient === me, (recipient) => recipient]
            ),
          ].join(", ");
        }
      }
      case EmailFolder.SENT: {
        return orderBy(
          getAllEmailRecipients(emailThread).map((recipientAddress) =>
            getEmailRecipientName(recipientAddress)
          ),
          [(recipient) => recipient !== me, (recipient) => recipient]
        ).join(", ");
      }
    }
  }, [intl, emailFolder, emailThread, getEmailRecipientName, getEmailSenderName]);

  const title = useMemo(() => {
    switch (emailFolder) {
      case EmailFolder.INBOX:
        return <SearchTextHighlighter text={recipients} />;
      case EmailFolder.SENT:
        return (
          <>
            <span>{`${intl.formatMessage(messages.emailToPrefix)}:`}&nbsp;</span>
            <SearchTextHighlighter text={recipients} />
          </>
        );
    }
  }, [intl, emailFolder, recipients]);

  const subject = <SearchTextHighlighter text={emailThread.subject} />;

  const emailFormattedDate = useMemo(() => {
    const emailDate = getEmailDate(emailThread);
    return formatDate(
      emailDate,
      isToday(emailDate) ? "p" : intl.formatMessage(messages.dateFormatDay)
    );
  }, [intl, emailThread]);

  const handleEmailClick = useCallback(() => {
    onOpenEmailThread(emailThread.id);
    analytics.clicked(["Open Email Thread"]);
  }, [analytics, emailThread, onOpenEmailThread]);

  return (
    <div className={styles.container} onClick={handleEmailClick} ref={containerRef}>
      <EmailUser className={styles.user} email={emailThread} user={emailUser} />
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.title}>
            <Tooltip title={title}>
              <span>{title}</span>
            </Tooltip>
          </div>
          <div className={styles.messagesCount}>{emailThread.messageCount}</div>
        </div>
        <div className={styles.subHeader}>
          <div className={styles.subject}>
            <Tooltip title={subject}>
              <span>{subject}</span>
            </Tooltip>
          </div>
        </div>
        <ParentEmailBodyPreview className={styles.emailBody} email={emailThread} />
      </div>
      <div className={styles.info}>
        <div className={styles.date}>{emailFormattedDate}</div>
        {emailFolder === EmailFolder.SENT && <EmailMetrics email={emailThread} />}
        <div className={styles.infoFooter}>
          <EmailAttachmentsIcon email={emailThread} />
          {emailFolder === EmailFolder.INBOX && emailThread.userReplied && (
            <FontAwesomeIcon className={styles.replyIcon} icon={faReply} />
          )}
          {emailFolder === EmailFolder.SENT && (
            <EmailActivity className={styles.activityIcon} email={emailThread} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  emailFolder: getParentEmailThreadsFolder(state),
});

const mapDispatchToProps = {
  onOpenEmailThread: openParentEmailThread,
};

export default connect(mapStateToProps, mapDispatchToProps)(ParentEmail);
